"use client";

import * as React from "react";
import { toast } from "@/hooks/use-toast";

/**
 * Cart item shape — normalized so any kind of product (BoutiqueSection Product,
 * PromotionsSection Promo, LaTableHyperOR row) can be added uniformly.
 */
export interface CartItem {
  id: string;
  name: string;
  price: number; // FCFA, unit price
  image?: string;
  origin?: string;
  boutiqueName?: string;
  qty: number;
}

interface CartState {
  items: CartItem[];
  isOpen: boolean;
}

type CartAction =
  | { type: "ADD"; item: Omit<CartItem, "qty">; qty?: number }
  | { type: "REMOVE"; id: string }
  | { type: "SET_QTY"; id: string; qty: number }
  | { type: "CLEAR" }
  | { type: "OPEN" }
  | { type: "CLOSE" }
  | { type: "HYDRATE"; items: CartItem[] };

const STORAGE_KEY = "hyper-or-cart-v1";

function reducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case "ADD": {
      const qty = action.qty ?? 1;
      const existing = state.items.find((it) => it.id === action.item.id);
      const items = existing
        ? state.items.map((it) =>
            it.id === action.item.id ? { ...it, qty: it.qty + qty } : it
          )
        : [...state.items, { ...action.item, qty }];
      return { ...state, items, isOpen: true };
    }
    case "REMOVE":
      return {
        ...state,
        items: state.items.filter((it) => it.id !== action.id),
      };
    case "SET_QTY": {
      const qty = Math.max(0, action.qty);
      const items = state.items
        .map((it) => (it.id === action.id ? { ...it, qty } : it))
        .filter((it) => it.qty > 0);
      return { ...state, items };
    }
    case "CLEAR":
      return { ...state, items: [] };
    case "OPEN":
      return { ...state, isOpen: true };
    case "CLOSE":
      return { ...state, isOpen: false };
    case "HYDRATE":
      return { ...state, items: action.items };
    default:
      return state;
  }
}

interface CartContextValue {
  items: CartItem[];
  isOpen: boolean;
  totalCount: number;
  totalPrice: number;
  addItem: (item: Omit<CartItem, "qty">, qty?: number) => void;
  removeItem: (id: string) => void;
  setQty: (id: string, qty: number) => void;
  clear: () => void;
  openCart: () => void;
  closeCart: () => void;
}

const CartContext = React.createContext<CartContextValue | null>(null);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = React.useReducer(reducer, {
    items: [],
    isOpen: false,
  });
  const [hydrated, setHydrated] = React.useState(false);

  // Hydrate from localStorage on mount (client-only).
  React.useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const items = JSON.parse(raw) as CartItem[];
        if (Array.isArray(items)) {
          dispatch({ type: "HYDRATE", items });
        }
      }
    } catch {
      // ignore malformed storage
    }
    setHydrated(true);
  }, []);

  // Persist to localStorage whenever items change (after hydration).
  React.useEffect(() => {
    if (!hydrated) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
    } catch {
      // ignore quota errors
    }
  }, [state.items, hydrated]);

  const totalCount = state.items.reduce((sum, it) => sum + it.qty, 0);
  const totalPrice = state.items.reduce(
    (sum, it) => sum + it.qty * it.price,
    0
  );

  const addItem = React.useCallback<CartContextValue["addItem"]>(
    (item, qty = 1) => {
      dispatch({ type: "ADD", item, qty });
      toast({
        title: "Ajouté au panier",
        description: item.name,
      });
    },
    []
  );

  const value: CartContextValue = {
    items: state.items,
    isOpen: state.isOpen,
    totalCount,
    totalPrice,
    addItem,
    removeItem: (id) => dispatch({ type: "REMOVE", id }),
    setQty: (id, qty) => dispatch({ type: "SET_QTY", id, qty }),
    clear: () => dispatch({ type: "CLEAR" }),
    openCart: () => dispatch({ type: "OPEN" }),
    closeCart: () => dispatch({ type: "CLOSE" }),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = React.useContext(CartContext);
  if (!ctx) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return ctx;
}
